import ChevronsIcon from '@assets/icons/chevrons-up-down.svg';
import i18n from '@/utils/i18n';
import React, { useMemo, useState } from 'react';
import SalesHubIcon from '@assets/icons/sales-hub.svg';
import StorefrontIcon from '@assets/icons/storefront.svg';
import { createStyles } from '@/contexts/theme-context';
import { DropdownMenu } from '@usepepper/ui/molecules/dropdown-menu';
import { DropdownTrigger } from '@usepepper/ui/drafts/dropdown-trigger';
import { ExperienceMode, useExperienceStore } from '@/stores/experience-store';
import { SIDEBAR_WIDTH } from '@experiences/containers/sidebar/base-sidebar';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useExperienceSwitcherProductsLoader } from '@experiences/containers/switch-experience-drawer/blocs/loaders/use-experience-switcher-products-loader';
import { View } from 'react-native';

const ICON_SIZE = 16;

interface ExperienceOption {
  icon: React.ReactNode;
  label: string;
  mode: ExperienceMode;
}

interface SwitchExperienceDrawerProps {
  onSwitchToStorefront?: () => void;
}

export const SwitchExperienceDrawer: React.FC<SwitchExperienceDrawerProps> = ({
  onSwitchToStorefront,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [styles, theme] = useStyles();
  const { mode, switchToExperience } = useExperienceStore();
  const { loading, products } = useExperienceSwitcherProductsLoader();

  const experiences = useMemo<ExperienceOption[]>(() => {
    const salesHub: ExperienceOption = {
      icon: (
        <SalesHubIcon
          height={ICON_SIZE}
          stroke={theme.sk.color.icon.secondary}
          width={ICON_SIZE}
        />
      ),
      label: i18n.t('sales_portal.sidebar.sales_hub'),
      mode: ExperienceMode.SALES_PORTAL,
    };
    const storefront: ExperienceOption = {
      icon: (
        <StorefrontIcon
          height={ICON_SIZE}
          stroke={theme.sk.color.icon.secondary}
          width={ICON_SIZE}
        />
      ),
      label: i18n.t('sales_portal.sidebar.storefront'),
      mode: ExperienceMode.OPERATOR_PORTAL,
    };

    const configured = products.map(({ icon: Icon, ...product }) => ({
      ...product,
      icon: Icon ? (
        <Icon color={theme.sk.color.icon.secondary} size={ICON_SIZE} />
      ) : null,
    }));

    return [salesHub, ...configured, storefront];
  }, [products, theme.sk.color.icon.secondary]);

  const currentExperience = experiences.find(
    (experience) => experience.mode === mode,
  );
  const fallbackExperience = loading ? undefined : experiences[0];
  const triggerExperience = currentExperience ?? fallbackExperience;

  const dropdownListData = useMemo(
    () =>
      experiences.map((experience) => {
        const isCurrent = experience.mode === mode;
        return {
          label: experience.label,
          leftElement: <View style={styles.avatar}>{experience.icon}</View>,
          onPress: () => {
            setIsDropdownOpen(false);
            if (!isCurrent) {
              if (experience.mode === ExperienceMode.OPERATOR_PORTAL) {
                onSwitchToStorefront?.();
              }
              switchToExperience(experience.mode);
            }
          },
          ...(isCurrent ? { style: styles.listItemDisabled } : {}),
        };
      }),
    [
      experiences,
      mode,
      onSwitchToStorefront,
      styles.avatar,
      styles.listItemDisabled,
      switchToExperience,
    ],
  );

  return (
    <DropdownMenu
      containerStyle={styles.dropdownContainer}
      isVisible={isDropdownOpen}
      listData={dropdownListData}
      onClose={() => setIsDropdownOpen(false)}
      position="top-start"
      trigger={
        <DropdownTrigger
          Icon={ChevronsIcon}
          isOpen={isDropdownOpen}
          onPress={() => setIsDropdownOpen(true)}
          style={styles.trigger}
        >
          <View style={styles.triggerContent}>
            <View style={styles.avatar}>{triggerExperience?.icon}</View>
            <View collapsable={false}>
              <Typography
                color={theme.sk.color.text.primary}
                numberOfLines={1}
                variant="paragraph-md"
              >
                {triggerExperience?.label ?? ''}
              </Typography>
            </View>
          </View>
        </DropdownTrigger>
      }
    />
  );
};

const useStyles = createStyles((theme) => ({
  avatar: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.secondary.default,
    borderRadius: theme.sk.radii.radiusSmall,
    height: theme.sk.spacing.spaceXlarge,
    justifyContent: 'center',
    width: theme.sk.spacing.spaceXlarge,
  },
  dropdownContainer: {
    width: SIDEBAR_WIDTH - theme.sk.spacing.spaceSmall * 3,
  },
  listItemDisabled: {
    cursor: 'auto',
    opacity: 0.5,
  },
  trigger: {
    borderWidth: 0,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  triggerContent: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
}));
